import config from "../../../config/config";
import { CERT_TEMPLATE_DEFAULTS } from "../constants/certificateTemplateOptions";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const BASE = `${config.BASE_URL}/certificate-templates`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) return "Internal server error. Please try again later.";
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

function buildApiError(res, payload, fallback) {
  const error = new Error(messageForResponse(res, payload, fallback));
  error.details = payload?.details || {};
  error.status = res.status;
  return error;
}

function parseContentDispositionFilename(header) {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function fetchCertificateTemplates() {
  const res = await authFetch(BASE, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, data, "Failed to load certificate templates");
  }
  return data;
}

export async function fetchCertificateTemplateById(id) {
  const res = await authFetch(`${BASE}/${encodeURIComponent(String(id))}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, data, "Failed to load certificate template");
  }
  return data;
}

export async function downloadCertificateTemplateImage(id) {
  const res = await authFetch(
    `${BASE}/${encodeURIComponent(String(id))}/download-image`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );

  const contentType = res.headers.get("Content-Type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const data = await parseJson(res);
      throw buildApiError(res, data, "Failed to download template image");
    }
    const text = await res.text();
    throw new Error(text || "Failed to download template image");
  }

  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ||
    `certificate-template-${id}.jpg`;
  triggerBrowserDownload(blob, filename);
  return { filename };
}

function slugFromPurposeName(name) {
  return String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildFormData(fields, file) {
  const fd = new FormData();
  fd.append("purpose_name", fields.purpose_name);
  fd.append(
    "purpose_slug",
    fields.purpose_slug?.trim() || slugFromPurposeName(fields.purpose_name),
  );
  fd.append("matter", fields.matter);
  fd.append(
    "name_font_family",
    fields.name_font_family || CERT_TEMPLATE_DEFAULTS.name_font_family,
  );
  fd.append("name_italic", fields.name_italic ? "1" : "0");
  fd.append("date_align", fields.date_align || CERT_TEMPLATE_DEFAULTS.date_align);
  if (file) fd.append("template_image", file);
  return fd;
}

export async function createCertificateTemplate(fields, file, csrfToken) {
  const headers = buildAuthHeaders(
    csrfToken ? { "X-CSRF-Token": csrfToken } : {},
  );
  delete headers["Content-Type"];

  const res = await authFetch(BASE, {
    method: "POST",
    credentials: "include",
    headers,
    body: buildFormData(fields, file),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, data, "Failed to create certificate template");
  }
  return data;
}

export async function updateCertificateTemplate(id, fields, file, csrfToken) {
  const headers = buildAuthHeaders(
    csrfToken ? { "X-CSRF-Token": csrfToken } : {},
  );
  delete headers["Content-Type"];

  const res = await authFetch(`${BASE}/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    credentials: "include",
    headers,
    body: buildFormData(fields, file),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, data, "Failed to update certificate template");
  }
  return data;
}

export async function deleteCertificateTemplate(id, csrfToken) {
  const res = await authFetch(`${BASE}/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw buildApiError(res, data, "Failed to deactivate certificate template");
  }
  return data;
}
